import {
  ColDef,
  ColDefField,
  ISetFilterParams,
  KeyCreatorParams,
  NestedFieldPaths,
  ValueFormatterFunc,
  ValueFormatterParams,
  ValueGetterParams,
  ValueParserParams,
} from 'ag-grid-community';
import { useAtomValue } from 'jotai';
import { get } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { AnyObject, ISchema, Maybe, ObjectSchema, SchemaDescription } from 'yup';
import { BooleanRenderer, BooleanRenderer2 } from '../components/AgTable/cell-renderers/BooleanRenderer';
import { GenericIdRenderer } from '../components/AgTable/cell-renderers/GenericIdRenderer';
import {
  RoundCellRenderer,
  RoundFromInvestmentCellRenderer,
} from '../components/AgTable/cell-renderers/RoundCellRenderer';
import { StringListCellRenderer } from '../components/AgTable/cell-renderers/StringListCellRenderer';
import {
  getFormatterIdToExcelStyleId,
  getRendererTypeToExcelStyleId,
} from '../components/AgTable/exportToExcelDefs';
import { CompanyIdCellRenderer } from '../components/grid-renderers/CompanyCellRenderer';
import { RendererType } from '../data-models/field-definition';
import { isNumericRenderer } from '../data-models/field2.data-model';
import { PrimitiveType } from '../data-models/field3.data-model';
import { IFormatterDataModel } from '../data-models/formatter.data-model';
import { IFundDataModel } from '../data-models/fund.data-model';
import { useGetCompanyIfSet } from '../pages/CompanyProfiles/hooks/useGetCompanyData';
import { getNumericColdDef, numericCellClasses } from '../pages/Finance2/common-grid-defs/commonColDefs';
import { FundProfileLinkRenderer } from '../pages/PortfolioOverview/components/OverviewTable/columns/TypeBasedConfigs/TypeCellRenderers/FundProfileLinkRenderer';
import { fundComparator } from '../pages/PortfolioOverview/state/FundFilterStateJ';
import { currencyMapByIdAtom, fundsByIdMapAtom } from '../services/state/AppConfigStateJ';
import { kpiTemplatesMapByUuidStateJ } from '../services/state/KPI/KPITemplatesStateJ';
import { IDisplayField } from '../view-models/display-field.view-model';
import { fieldInCurrency } from './formatters/currency-model';
import { createFormField, IFormField } from '../view-models/form.view-model';
import { IGridField } from '../view-models/grid.view-model';
import { AgFormatterService } from './ag-formatter-service';
import { getIsoDateColDef, getIsoDateTimeColDef } from './ag-grid-utils';
import { getCompactExcelCellClassRules, getExcelFormatterId } from './excel-formatter-service';
import { declaresDate, isDateSchemaField } from './schema-data-type';
import { FormatterService, StandardFormatterId } from './formatter-service';
import { BooleanFormattersConfig } from './formatters/BooleanFormatters';
import { NumericFormatterId } from './formatters/NumericFormatters';
import { getForesightStore } from './jotai-store';
import { ParserService, supportedNumberFormats } from './parser-service';
import { camelCaseToCapitalizedString } from './stringUtils';

const PrimitiveTypeToRenderer: Record<PrimitiveType, RendererType> = {
  array: RendererType.multiSelect,
  boolean: RendererType.boolean,
  date: RendererType.date,
  number: RendererType.number,
  string: RendererType.text,
};

export function useSchemaToGrid() {
  const schemaFieldToColDef = useSchemaToColDef();

  return useCallback(
    <T extends Maybe<AnyObject>>(
      schema: ObjectSchema<T>,
      fieldsToShow?: ColDefField<T>[],
      // Money fields format in this currency; absent keeps the schema's own formatter.
      currencyCode?: string | null
    ): ColDef<T>[] => {
      return (fieldsToShow ?? Object.keys(schema.fields)).map((fieldId) => {
        const field = get(schema.fields, getSchemaFieldsPath(fieldId));
        const desc = field.describe() as SchemaDescription;

        return schemaFieldToColDef<T>(fieldId as ColDefField<T>, desc, currencyCode);
      });
    },
    [schemaFieldToColDef]
  );
}

export { schemaFieldDataType } from './schema-data-type';

function getRendererForField(fieldDescription: SchemaDescription) {
  if (isDateSchemaField(fieldDescription) || declaresDate(fieldDescription)) {
    return RendererType.date;
  }
  return PrimitiveTypeToRenderer[fieldDescription.type as PrimitiveType];
}

export function useSchemaToColDef() {
  const getExtendedColDefFromGridField = useGetExtendedColDefFromGridField();

  return useCallback(
    <T>(fieldId: ColDefField<T>, fieldDescription: SchemaDescription, currencyCode?: string | null) => {
      const meta = fieldDescription.meta as Partial<IGridField<unknown>>;
      const type = fieldDescription.type as PrimitiveType;

      const field: IGridField<unknown> = fieldInCurrency(
        {
          dataType: type,
          key: meta?.key ?? fieldId,
          renderer: getRendererForField(fieldDescription),
          ...fieldDescription,
          ...meta,
        } as IGridField<unknown>,
        currencyCode
      );

      return {
        ...getBaseColDefFromGridField<T>(field),
        ...getExtendedColDefFromGridField<T>(field),
      } as ColDef<T>;
    },
    [getExtendedColDefFromGridField]
  );
}

export function getBaseColDefFromGridField<T>(gridField: IGridField<unknown>): Partial<ColDef<T>> {
  const { key, label, aggFunc, editable, enableRowGroup, cellRenderer, hide } = gridField;

  return {
    aggFunc,
    colId: key,
    editable,
    enableRowGroup,
    field: key,
    headerName: label,
    headerTooltip: label,
    cellRenderer,
    hide,
    valueParser: fieldToParser(gridField),
    cellDataType: gridField.cellDataType,
  } as Partial<ColDef<T>>;
}

export function fieldToParser(field: IGridField<unknown>) {
  const parser = ParserService.get();
  const formatter = field.formatter;
  switch (true) {
    case formatter === 'fund':
      return (params: ValueParserParams) => parser.parse('fund', params.newValue);
    case supportedNumberFormats.has(field.formatter as NumericFormatterId):
    case isNumericRenderer(field.renderer):
      return (params: ValueParserParams) => parser.parse('number', params.newValue);
  }
}

function useGetExtendedColDefFromGridField() {
  const gridFormatters = useGridFormatters();
  const companyNameFromIdComparator = useCompanyNameFromIdComparator();

  return useCallback(
    <T>(gridField: IGridField<unknown>): ColDef<T> => {
      const agFormatter = AgFormatterService.get()?.getFormatterForFormField<T, unknown>(gridField);
      const formatter = FormatterService.get().getFormatterForFormField(gridField);
      const formatterId = gridField.formatter as StandardFormatterId;
      // Abbreviated formatters pick their Excel style per cell, which also keeps them off the
      // getFormatterIdToExcelStyleId mapping that otherwise collapses them onto plain currency.
      const compactExcelRules = getCompactExcelCellClassRules(formatterId);
      const style = compactExcelRules
        ? null
        : (getFormatterIdToExcelStyleId(formatterId) ?? getRendererTypeToExcelStyleId(gridField.renderer));

      if (gridField.formatter === 'kpiTemplateName') {
        return {
          keyCreator: gridFormatters.templateNameFormatter,
          minWidth: 200,
          valueFormatter: gridFormatters.templateNameFormatter,
          filterParams: {
            valueFormatter: gridFormatters.templateNameFormatter,
          },
        } as ColDef<T>;
      }

      if (gridField.formatter === 'fund') {
        return {
          cellRenderer: FundProfileLinkRenderer,
          comparator: fundIdComparator,
          keyCreator: AgFormatterService.get()?.getFormatterForId('fund'),
          valueFormatter: AgFormatterService.get()?.getFormatterForId('fund'),
          filterParams: {
            valueFormatter: AgFormatterService.get()?.getFormatterForId('fund'),
          },
        };
      }

      switch (gridField.renderer) {
        case RendererType.boolean: {
          return {
            cellRenderer:
              (gridField.formatter as IFormatterDataModel<unknown>)?.id ===
              BooleanFormattersConfig.trueFalse.id
                ? BooleanRenderer2
                : BooleanRenderer,
            filter: 'agSetColumnFilter',
            filterParams: {
              valueFormatter: agFormatter,
            } as ISetFilterParams,
            keyCreator: (params: KeyCreatorParams<T, boolean>) => {
              return formatter(params.value ?? false);
            },
            valueFormatter: agFormatter,
            valueGetter: (params: ValueGetterParams<T, boolean>) => {
              return get(params.data, gridField.key);
            },
          };
        }

        case RendererType.companyId:
          return {
            ...generateIdDataColDefs(gridFormatters.companyNameFromIdFormatter),
            keyCreator: gridFormatters.companyNameFromIdFormatter,
            minWidth: 200,
            valueFormatter: gridFormatters.companyNameFromIdFormatter,
            cellRenderer: CompanyIdCellRenderer,
            comparator: companyNameFromIdComparator,
          };

        case RendererType.currency:
          return {
            ...getNumericColdDef(),
            cellClass: [getExcelFormatterId('currency') ?? '', 'monospace', 'text-align-right'],
            valueFormatter: gridFormatters.currencyFormatter as ValueFormatterFunc<T, number>,
          };

        case RendererType.currencyId:
          return {
            cellClass: ['text-align-right'],
            valueFormatter: gridFormatters.currencyNameFromIdFormatter,
          };

        case RendererType.date: {
          if (formatterId === 'dateTime') {
            // supported from ag-grid v34
            return getIsoDateTimeColDef({
              valueFormatter: agFormatter,
            });
          } else {
            return getIsoDateColDef({
              valueFormatter: agFormatter,
            });
          }
        }
        case RendererType.id: {
          const formatterId = gridField.formatter as StandardFormatterId;
          const commonDefs = generateIdDataColDefs<T>(
            AgFormatterService.get().getFormatterForId(formatterId)
          );

          if (formatterId === 'round') {
            return {
              ...commonDefs,
              cellRenderer: RoundCellRenderer,
            };
          }

          if (formatterId === 'stage') {
            return {
              ...commonDefs,
              cellRenderer: RoundFromInvestmentCellRenderer,
            };
          }

          return { ...commonDefs };
        }

        case RendererType.number:
        case RendererType.integer:
        case RendererType.multiplier:
        case RendererType.percent: {
          const cellClass = style ? [...numericCellClasses, style] : numericCellClasses;
          return {
            ...getNumericColdDef(),
            valueFormatter: agFormatter,
            cellClass,
            ...(compactExcelRules ? { cellClassRules: compactExcelRules } : {}),
          };
        }

        case RendererType.multiSelect: {
          return {
            cellRenderer: StringListCellRenderer,
            cellRendererParams: {
              title: gridField.label ?? camelCaseToCapitalizedString(gridField.key),
            },
          };
        }

        default:
          return {
            valueFormatter: agFormatter,
          };
      }
    },
    [gridFormatters, companyNameFromIdComparator]
  );
}

export function useGridFormatters() {
  const currencyMap = useAtomValue(currencyMapByIdAtom);
  const getCompany = useGetCompanyIfSet();
  useAtomValue(kpiTemplatesMapByUuidStateJ);

  return useMemo(() => {
    return {
      companyNameFromIdFormatter: <T>({ value: companyId }: ValueFormatterParams<T, number>) => {
        const company = getCompany(companyId ?? -1);
        return company?.name ?? '';
      },
      currencyFormatter: <T extends { currencyid?: number }>(params: ValueFormatterParams<T, number>) => {
        const currencyCode = currencyMap.get(params.data?.currencyid ?? -1)?.code ?? 'USD';
        if (typeof params.value !== 'number') {
          return '';
        }

        const formatter = FormatterService.get().getFormatterForModel({
          type: 'number',
          id: `${currencyCode}-long`,
          config: {
            currency: currencyCode,
            style: 'currency',
          },
        });

        return formatter(params.value);
      },
      currencyNameFromIdFormatter: <T>({ value }: ValueFormatterParams<T, number>) =>
        currencyMap.get(value ?? -1)?.code ?? '',
      templateNameFormatter: AgFormatterService.get().getFormatterForId<unknown, string>('kpiTemplateName'),
    };
  }, [currencyMap, getCompany]);
}

export function useCompanyNameFromIdComparator() {
  const getCompany = useGetCompanyIfSet();

  return useCallback(
    (valueA: number, valueB: number) => {
      const companyA = getCompany(valueA ?? -1);
      const companyB = getCompany(valueB ?? -1);
      if (companyA?.name && companyB?.name) {
        return companyA.name.toLocaleLowerCase().localeCompare(companyB.name.toLocaleLowerCase());
      }
      return (valueA ?? Number.MAX_SAFE_INTEGER) - (valueB ?? Number.MAX_SAFE_INTEGER);
    },
    [getCompany]
  );
}

function fundIdComparator(a: number, b: number) {
  const store = getForesightStore();
  const fundsById = store.get(fundsByIdMapAtom);

  return fundComparator(
    fundsById.get(a) ?? ({} as IFundDataModel),
    fundsById.get(b) ?? ({} as IFundDataModel)
  );
}

export function useSchemaToFormFields() {
  return useCallback(schemaToFormFields, []);
}

export function schemaToFormFields<T extends Maybe<AnyObject>>(
  schema: ObjectSchema<T>,
  fieldsToShow?: NestedFieldPaths<T>[] | Extract<keyof T, string>[]
): IFormField<unknown>[] {
  return (fieldsToShow ?? Object.keys(schema.fields)).map((fieldId) => {
    const yupField = get(
      schema.fields,
      getSchemaFieldsPath(fieldId as string)
    ).describe() as SchemaDescription;
    const formField = (yupField.meta as Partial<IFormField<unknown>>) ?? {};
    const dataType = formField.dataType ?? (yupField.type as PrimitiveType);
    const renderer = formField.renderer ?? fieldTypeToRendererType(dataType);

    return createFormField({
      required: !yupField.optional,
      key: fieldId,
      label: yupField.label ?? camelCaseToCapitalizedString(fieldId),
      dataType,
      renderer,
      ...formField,
    });
  });
}

export function fieldTypeToRendererType(type: string): RendererType {
  switch (type) {
    case 'array':
      return RendererType.multiSelect;
    case 'boolean':
      return RendererType.boolean;
    case 'date':
      return RendererType.date;
    case 'number':
      return RendererType.number;
    case 'string':
      return RendererType.text;
    default:
      return RendererType.text;
  }
}

export function getSchemaFieldsPath(path: string) {
  const pathParts = path.split('.');
  return pathParts.join('.fields.');
}

export function generateIdDataColDefs<T>(
  formatter: (params: ValueFormatterParams<T>) => string
): Pick<ColDef<T>, 'keyCreator' | 'valueFormatter' | 'comparator' | 'cellRenderer' | 'filterParams'> {
  return {
    keyCreator: formatter,
    valueFormatter: formatter,
    cellRenderer: GenericIdRenderer,
    filterParams: {
      valueFormatter: formatter,
      keyCreator: formatter,
    },
  };
}

export function formatFieldValue<T>(
  fieldSchema: ISchema<T>,
  value?: T | null,
  noValue?: string,
  currencyCode?: string | null
): string {
  const desc = fieldSchema.describe() as SchemaDescription;
  const field = (desc.meta as IDisplayField<unknown>) ?? {
    formatter: 'string',
  };
  const formatter = FormatterService.get().getFormatterForFormField(fieldInCurrency(field, currencyCode));

  return value != null ? formatter(value) : (noValue ?? '');
}

interface ILabelAndFormattedValue {
  label: string;
  formattedValue: string;
}
export function fieldDisplayValuesFromSchema<T extends AnyObject>({
  schema,
  paths,
  values,
  currencyCode,
}: {
  schema: ObjectSchema<T>;
  paths: (keyof T)[];
  values: T;
  /** Money fields format in this currency instead of the formatter's own. */
  currencyCode?: string | null;
}): Map<string, ILabelAndFormattedValue> {
  return paths.reduce((map, path) => {
    const schemaField = get(schema.fields, path);
    if (!schemaField) {
      return map;
    }

    return map.set(String(path), {
      label:
        (schemaField.describe() as SchemaDescription)?.label ?? camelCaseToCapitalizedString(path as string),
      formattedValue: formatFieldValue(
        schemaField as ISchema<unknown>,
        get(values, path),
        undefined,
        currencyCode
      ),
    });
  }, new Map<string, ILabelAndFormattedValue>());
}
